import * as path from "node:path";
import { TsDocCoverageRecord } from "../../tsdoc-coverage/coverage";
import {
  AuditCategory,
  AuditFinding,
  AuditSeverity,
  TsdocCoverageFinding,
} from "../audit-report";
import { AuditCheckRunner, AuditCheckRunnerContext } from "../run-scan";

/**
 * Options accepted by {@link createTsdocCoverageCheck}.
 *
 * The runner consumes already-computed coverage records produced by
 * `analyzeTsDocCoverage()`. The factory deliberately does not invoke
 * the analyzer itself — that keeps the runner cheap to test (pass an
 * inline records array) and keeps the orchestrator free to compute
 * coverage once and feed multiple checks from the same input.
 */
export interface TsdocCoverageCheckOptions {
  /**
   * The coverage records to translate into findings. Typically the
   * concatenated output of `analyzeTsDocCoverage()` for every public
   * package the orchestrator is scanning. The factory may be called
   * with an empty array when no public exports were inspected — the
   * resulting runner produces no findings.
   */
  readonly records: ReadonlyArray<TsDocCoverageRecord>;

  /**
   * Stable name surfaced via {@link AuditCheckRunner.name}. Defaults
   * to `"tsdocCoverage"` so logs match the audit-report category key.
   *
   * @default "tsdocCoverage"
   */
  readonly name?: string;
}

/**
 * Map a single {@link TsDocCoverageRecord} to zero, one, or more
 * audit findings. Exported so callers can reuse the
 * record-to-finding mapping without going through a runner (e.g. the
 * audit-mode walker in #526 may compose findings differently).
 *
 * Severity mapping (per the parent epic's drift-handling matrix):
 *
 * - `missing-summary` — `mechanical` (Phase 2 can auto-stub).
 * - `thin-summary` — `advisory` (a human should rewrite).
 * - `missing-params` / `missing-returns` — `advisory` (the symbol
 *   does carry a summary; the missing block tag is signal but does
 *   not block).
 *
 * A record with `hasSummary && !hasThinSummary` and no missing block
 * tags produces no findings — fully-documented exports are reported
 * by their absence in the result.
 */
export function tsdocRecordToFindings(
  record: TsDocCoverageRecord,
  context: AuditCheckRunnerContext,
): Array<TsdocCoverageFinding> {
  const findings: Array<TsdocCoverageFinding> = [];
  const file = relativizeFile(record.location.file, context.repoRoot);
  const line = record.location.line;
  const symbol = record.symbol;

  if (!record.hasSummary) {
    findings.push({
      category: AuditCategory.TsdocCoverage,
      severity: AuditSeverity.Mechanical,
      location: { file, line },
      subject: symbol,
      details: `Public export \`${symbol}\` is missing a TSDoc summary.`,
      shortfall: "missing-summary",
      symbol,
      fixHint: "stub-tsdoc",
    });
    // When the summary is missing entirely the missing-params /
    // missing-returns shortfalls are subsumed — the stub Phase 2
    // emits will scaffold the block tags too. Skip the noise.
    return findings;
  }

  if (record.hasThinSummary) {
    findings.push({
      category: AuditCategory.TsdocCoverage,
      severity: AuditSeverity.Advisory,
      location: { file, line },
      subject: symbol,
      details: `Public export \`${symbol}\` carries a thin TSDoc summary; consider expanding it.`,
      shortfall: "thin-summary",
      symbol,
    });
  }

  // Only flag missing block tags on kinds that can carry them. The
  // coverage utility already returns `false` for kinds without
  // params or returns (TypeAlias, Variable, etc.) so this logic
  // collapses naturally.
  if (!record.hasParams && isParamCarryingKind(record)) {
    findings.push({
      category: AuditCategory.TsdocCoverage,
      severity: AuditSeverity.Advisory,
      location: { file, line },
      subject: symbol,
      details: `Public export \`${symbol}\` has parameters with no \`@param\` block tag.`,
      shortfall: "missing-params",
      symbol,
    });
  }

  if (!record.hasReturns && isReturnCarryingKind(record)) {
    findings.push({
      category: AuditCategory.TsdocCoverage,
      severity: AuditSeverity.Advisory,
      location: { file, line },
      subject: symbol,
      details: `Public export \`${symbol}\` has a return value with no \`@returns\` block tag.`,
      shortfall: "missing-returns",
      symbol,
    });
  }

  return findings;
}

/**
 * Build an {@link AuditCheckRunner} that emits TSDoc-coverage
 * findings from a precomputed list of {@link TsDocCoverageRecord}.
 * The runner is synchronous and idempotent; calling `run()` twice
 * produces identical findings.
 */
export function createTsdocCoverageCheck(
  options: TsdocCoverageCheckOptions,
): AuditCheckRunner {
  const records = options.records;
  return {
    name: options.name ?? "tsdocCoverage",
    run(context: AuditCheckRunnerContext): ReadonlyArray<AuditFinding> {
      const findings: Array<TsdocCoverageFinding> = [];
      for (const record of records) {
        for (const finding of tsdocRecordToFindings(record, context)) {
          findings.push(finding);
        }
      }
      return findings;
    },
  };
}

/**
 * Compiler kinds that carry a parameter list. `Function` covers
 * function and method declarations; classes carry constructors but
 * the coverage utility already records the class itself, so we
 * intentionally do not request `@param` on class declarations here.
 */
function isParamCarryingKind(record: TsDocCoverageRecord): boolean {
  return record.kind === "Function";
}

/**
 * Compiler kinds that carry a return value worth documenting. Same
 * scope as {@link isParamCarryingKind} — `Function` only.
 */
function isReturnCarryingKind(record: TsDocCoverageRecord): boolean {
  return record.kind === "Function";
}

function relativizeFile(file: string, repoRoot: string): string {
  if (!file) {
    return "";
  }
  if (!path.isAbsolute(file)) {
    return toPosix(file);
  }
  const rel = path.relative(repoRoot, file);
  return toPosix(rel);
}

function toPosix(p: string): string {
  return p.split(path.sep).join("/");
}
